import React, { useEffect, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import {
  mdiMagnify,
  mdiChevronDown,
  mdiChevronUp,
  mdiCloseCircleOutline,
} from "@mdi/js";
import InwardOrdersTable from "../inward/InwardOrdersTable";
import LoadingWidget from "../../../../context_providers/modal/loader";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import {
  useWarehouseInward,
  WarehouseInwardProvider,
} from "../contexts/InwardContext";
import OutwardOrdersTable from "../outward/OutwardOrdersTable";
import PendingActionsTable from "./pending_actions_table";
import GenerateDCModal from "./generate_challan_modal";
import {
  fetchExportOutwardByEmail,
  useWarehouseOutward,
  WarehouseOutwardProvider,
} from "../contexts/OutwardContext";
import { exportInwardByEmail } from "../../../../erp/accounts/vouchers/purchase/service/inward_repo";
import { mdiEmailOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import RejectSTModal from "./RejectSTModal";
import OutwardViewModal from "../stock-transfer/stock_transfer_view_modal";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";
import MultiSelectDropdown from "../common-components/MultiSelectDropdown";
import CustomerGroupSearchField from "../common-components/CustomerGroupSearchField";
import ItemSearchTagInput from "../common-components/ItemSearchTagInput";
import UploadInvModal from "./upload_inv_modal";
import UploadReverseDcDocsModal from "./upload_reverse_dc_docs_modal";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";
import { ToggleEwayWaiverApi } from "../warehouse_module_repo";

type TabType = "finance-pending" | "finance-outward" | "finance-inward";

const WarehouseFinanceDashboardWrapper = () => {
  const [activeTab, setActiveTab] = useState<TabType>("finance-pending");

  return (
    <WarehouseOutwardProvider
      config={{
        initialTab: "finance-pending",
        autoFetch: true,
        storageKey: "warehouse:finance-outward:filters:v2",
      }}
    >
      {activeTab === "finance-inward" ? (
        <WarehouseInwardProvider storageKey="warehouse:finance-inward:filters:v2">
          <WarehouseFinanceDashboard
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </WarehouseInwardProvider>
      ) : (
        <WarehouseFinanceDashboard
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </WarehouseOutwardProvider>
  );
};

export default WarehouseFinanceDashboardWrapper;

interface Props {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

const WarehouseFinanceDashboard: React.FC<Props> = ({
  activeTab,
  setActiveTab,
}) => {
  const outwardContext = useWarehouseOutward();
  const inwardContext = useWarehouseInward();
  const isWarehouseViewOnly = useWarehouseViewOnly();

  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [cascade, setCascade] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const f: any = filters || {};
      let res: any;
      if (activeTab === "finance-inward") {
        res = await exportInwardByEmail({
          search: f.search,
          status: f.status?.length ? f.status.join(",") : undefined,
          type: f.type?.length ? f.type.join(",") : undefined,
          fromDate: f.fromDate,
          toDate: f.toDate,
          fromWarehouse: f.fromWarehouse,
          toWarehouse: f.toWarehouse,
          itemSearch: f.itemSearch,
          sortBy: f.sortBy,
          sortOrder: f.sortOrder,
          source: "finance",
        });
      } else {
        res = await fetchExportOutwardByEmail({
          search: f.search,
          status: f.status?.length ? f.status.join(",") : undefined,
          type: f.type?.length ? f.type.join(",") : undefined,
          fromDate: f.fromDate,
          toDate: f.toDate,
          fromWarehouse: f.fromWarehouse,
          toWarehouse: f.toWarehouse,
          itemSearch: f.itemSearch,
          customerGroup: f.customerGroup,
          tab: activeTab,
          sortBy: f.sortBy,
          sortOrder: f.sortOrder,
          includeDispatchedDate: true,
        });
      }
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isGenerateDCModalOpen, setGenerateDCModalOpen] = useState(false);
  const [isUploadInvModalOpen, setUploadInvModalOpen] = useState(false);
  const [isReverseDcDocsModalOpen, setReverseDcDocsModalOpen] = useState(false);
  const [selectedOutward, setSelectedOutward] = useState<any>(null);
  const [isRejectSTModalOpen, setIsRejectSTModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setShowFilters(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeTab === "finance-inward") return;
    outwardContext.setFilters((prev) => ({
      ...prev,
      tab: activeTab,
    }));
    outwardContext.setPage(1);
  }, [activeTab]);

  const isLoading =
    activeTab === "finance-inward"
      ? inwardContext?.isLoading
      : outwardContext?.isLoading;
  const filters =
    activeTab === "finance-inward"
      ? inwardContext?.filters
      : outwardContext?.filters;
  const setFilters =
    activeTab === "finance-inward"
      ? inwardContext?.setFilters
      : outwardContext?.setFilters;
  const page =
    activeTab === "finance-inward" ? inwardContext?.page : outwardContext?.page;
  const setPage =
    activeTab === "finance-inward"
      ? inwardContext?.setPage
      : outwardContext?.setPage;
  const pagination =
    activeTab === "finance-inward"
      ? inwardContext?.pagination
      : outwardContext?.pagination;
  const statusCount = outwardContext?.statusCount;

  const outwardList = outwardContext?.outwardList || [];
  const inwardList = inwardContext?.inwardList || [];
  const refreshOutwards = outwardContext?.refreshOutwards;

  const openViewModal = (outward: any) => {
    setSelectedOutward(outward);
    setViewModalOpen(true);
  };

  const openGenerateDCModal = (outward: any) => {
    setSelectedOutward(outward);
    setGenerateDCModalOpen(true);
  };

  const openUploadInvModal = (outward: any) => {
    setSelectedOutward(outward);
    setUploadInvModalOpen(true);
  };

  const openReverseDcDocsModal = (outward: any) => {
    setSelectedOutward(outward);
    setReverseDcDocsModalOpen(true);
  };

  const openRejectModal = (outward: any) => {
    setSelectedOutward(outward);
    setIsRejectSTModalOpen(true);
  };

  const handleToggleEway = async (outward: any, waived: boolean) => {
    try {
      const res: any = await ToggleEwayWaiverApi(outward.id, waived);
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text:
            res.message ||
            (waived ? "E-way waived" : "E-way marked as required"),
        });
        refreshOutwards?.();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to update e-way waiver",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to update e-way waiver",
      });
    }
  };

  const closeModals = () => {
    setViewModalOpen(false);
    setGenerateDCModalOpen(false);
    setIsRejectSTModalOpen(false);
    setSelectedOutward(null);
  };

  return (
    <main className="wh-mobile-shrink p-4 md:p-6">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <h1 className="text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
          Finance Dashboard
        </h1>
        <button
          id="warehouse-finance-export-email-btn"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Icon path={mdiEmailOutline} size={0.8} />
          {exporting ? "Scheduling…" : "Export by Email"}
        </button>
      </div>

      <div className="space-y-6">
        {statusCount && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              {
                label: "Document Pending",
                value: statusCount["Doc Pending"],
                subLabel: "ST < ₹50k",
                color: "border-purple-500",
              },
              {
                label: "Ready to Dispatch",
                value: statusCount["Ready To Dispatch"],
                color: "border-green-500",
              },
              {
                label: "Dispatched",
                value: statusCount["Dispatched"],
                color: "border-yellow-600",
              },
              {
                label: "Rejected",
                value: statusCount["Rejected"],
                color: "border-purple-500",
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`rounded-lg border-l-4 bg-white text-card-foreground shadow-sm ${item.color}`}
              >
                <div className="p-4">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-2xl font-bold mt-2">{item.value}</p>
                  {item.subLabel && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.subLabel}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tab Selector */}
      <div className="flex gap-2 bg-blue-50 p-2 rounded-md mb-4">
        {[
          {
            key: "finance-pending",
            label: `Pending Actions (${statusCount["Doc Pending"]}) `,
          },
          { key: "finance-outward", label: "All Outward" },
          { key: "finance-inward", label: "All Inward" },
        ].map((t) => (
          <button
            key={t.key}
            id={`warehouse-finance-tab-${t.key}-btn`}
            onClick={() => setActiveTab(t.key as TabType)}
            className={`px-4 py-2 rounded text-sm font-bold ${
              activeTab === t.key ? "bg-white shadow-sm" : "text-gray-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters (only for pending tab) */}
      {activeTab === "finance-pending" && (
        <div className="flex items-center justify-between mb-4">
          <div
            id="warehouse-finance-filters-toggle"
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setShowFilters((p) => !p)}
          >
            <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              Filters
              <Icon
                path={showFilters ? mdiChevronUp : mdiChevronDown}
                size={0.8}
              />
            </div>
          </div>

          {(filters.search ||
            filters.type?.length ||
            filters.status?.length ||
            filters.fromDate ||
            filters.toDate ||
            filters.fromWarehouse ||
            filters.toWarehouse ||
            filters.itemSearch ||
            (filters as any).customerGroup) && (
            <button
              id="warehouse-finance-clear-filters-btn"
              onClick={() =>
                setFilters((prev: any) => ({
                  ...prev,
                  search: "",
                  type: [],
                  status: [],
                  fromDate: "",
                  toDate: "",
                  fromWarehouse: "",
                  toWarehouse: "",
                  itemSearch: "",
                  customerGroup: "",
                }))
              }
              className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
            >
              <Icon path={mdiCloseCircleOutline} size={0.6} />
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {showFilters && (
        <div className="rounded-lg border border-gray-300 shadow-sm bg-white mb-4">
          {/* ROW 1 */}
          <div className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex items-center gap-2">
                <div className="relative flex-1">
                  <Icon
                    path={mdiMagnify}
                    className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                  />

                  <input
                    id="warehouse-finance-search-input"
                    className="flex h-10 w-full rounded-md border bg-background px-3 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Search by Order ID..."
                    value={filters.search || ""}
                    onChange={(e) =>
                      setFilters((prev: any) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                  />
                </div>

                {filters.search && (
                  <button
                    id="warehouse-finance-clear-search-btn"
                    onClick={() =>
                      setFilters((prev: any) => ({
                        ...prev,
                        search: "",
                      }))
                    }
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Clear Search"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              <MultiSelectDropdown
                placeholder="All Types"
                values={filters.type || []}
                onChange={(next) =>
                  setFilters((prev: any) => ({
                    ...prev,
                    type: next,
                  }))
                }
                options={
                  activeTab === "finance-inward"
                    ? [
                        { value: "PO", label: "Purchase Order" },
                        { value: "ST", label: "Stock Transfer" },
                      ]
                    : [
                        { value: "SO", label: "Sales Order" },
                        { value: "ST", label: "Stock Transfer" },
                      ]
                }
              />
            </div>
          </div>

          {/* ROW 2 */}
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* From Date */}
              <div className="flex items-center gap-2">
                <input
                  id="warehouse-finance-from-date-input"
                  type="date"
                  value={filters.fromDate || ""}
                  onChange={(e) =>
                    setFilters((p: any) => ({
                      ...p,
                      fromDate: e.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border px-3 text-sm"
                  placeholder="From Date"
                />

                {filters.fromDate && (
                  <button
                    id="warehouse-finance-clear-from-date-btn"
                    onClick={() =>
                      setFilters((p: any) => ({
                        ...p,
                        fromDate: "",
                      }))
                    }
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              {/* To Date */}
              <div className="flex items-center gap-2">
                <input
                  id="warehouse-finance-to-date-input"
                  type="date"
                  value={filters.toDate || ""}
                  onChange={(e) =>
                    setFilters((p: any) => ({
                      ...p,
                      toDate: e.target.value,
                    }))
                  }
                  className="h-10 w-full rounded-md border px-3 text-sm"
                  placeholder="To Date"
                />

                {filters.toDate && (
                  <button
                    id="warehouse-finance-clear-to-date-btn"
                    onClick={() =>
                      setFilters((p: any) => ({
                        ...p,
                        toDate: "",
                      }))
                    }
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Icon path={mdiCloseCircleOutline} size={0.7} />
                  </button>
                )}
              </div>

              {/* From Warehouse */}
              <WarehouseSearchFieldByNameID
                placeholder="From Warehouse (Name / ID)"
                value={filters.fromWarehouse || ""}
                onChange={(val) =>
                  setFilters((p: any) => ({
                    ...p,
                    fromWarehouse: val,
                  }))
                }
                onClear={() =>
                  setFilters((p: any) => ({
                    ...p,
                    fromWarehouse: "",
                  }))
                }
              />

              {/* To Warehouse */}
              <WarehouseSearchFieldByNameID
                placeholder="To Warehouse (Name / ID)"
                value={filters.toWarehouse || ""}
                onChange={(val) =>
                  setFilters((p: any) => ({
                    ...p,
                    toWarehouse: val,
                  }))
                }
                onClear={() =>
                  setFilters((p: any) => ({
                    ...p,
                    toWarehouse: "",
                  }))
                }
              />
            </div>
          </div>

          {/* ROW 3 */}
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 items-center">
              {/* 50% */}
              <div className="md:col-span-2">
                <ItemSearchTagInput
                  value={filters.itemSearch || ""}
                  onChange={(next) =>
                    setFilters((p: any) => ({
                      ...p,
                      itemSearch: next,
                    }))
                  }
                />
              </div>

              {/* Customer group is a customer-master attribute, so it only
                  applies to the outward (SO) tabs. */}
              {activeTab !== "finance-inward" && (
                <CustomerGroupSearchField
                  value={(filters as any).customerGroup || ""}
                  onChange={(val) =>
                    setFilters((p: any) => ({
                      ...p,
                      customerGroup: val,
                    }))
                  }
                  onClear={() =>
                    setFilters((p: any) => ({
                      ...p,
                      customerGroup: "",
                    }))
                  }
                />
              )}

              <div className="inline-flex items-center gap-2 h-10 select-none">
                <input
                  id="warehouse-finance-expand-items-checkbox"
                  type="checkbox"
                  checked={cascade}
                  onChange={(e) => setCascade(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />

                <span className="text-sm text-gray-700">Expand items</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PENDING ACTIONS TAB */}
      {activeTab === "finance-pending" && (
        <PendingActionsTable
          outwards={outwardList}
          loading={isLoading || false}
          actionsAllowed={!isWarehouseViewOnly}
          itemSearch={filters?.itemSearch}
          cascade={cascade}
          sortBy={filters?.sortBy}
          sortOrder={filters?.sortOrder}
          currentPage={page}
          totalRecords={pagination?.total}
          limit={pagination?.limit}
          onSortByValue={() =>
            setFilters?.((p: any) => {
              if (p?.sortBy !== "value")
                return { ...p, sortBy: "value", sortOrder: "asc" };
              if (p?.sortOrder === "asc")
                return { ...p, sortBy: "value", sortOrder: "desc" };
              return { ...p, sortBy: undefined, sortOrder: undefined };
            })
          }
          onViewClick={openViewModal}
          onGenerateDCClick={openGenerateDCModal}
          onUploadInvClick={openUploadInvModal}
          onUploadReverseDcDocsClick={openReverseDcDocsModal}
          onToggleEwayClick={handleToggleEway}
          onRejectClick={openRejectModal}
        />
      )}

      {/* ALL OUTWARD TAB */}
      {activeTab === "finance-outward" &&
        outwardList &&
        outwardList.length > 0 && (
          <OutwardOrdersTable
            updatesAllowed={false}
            title={"All Outward Orders"}
            cascade={cascade}
          />
        )}

      {/* ALL INWARD TAB */}
      {activeTab === "finance-inward" &&
        inwardList &&
        inwardList.length > 0 && (
          <InwardOrdersTable updatesAllowed={false} cascade={cascade} />
        )}

      {activeTab === "finance-inward" &&
        inwardList &&
        inwardList.length === 0 &&
        !isLoading && (
          <p className="text-center text-muted-foreground mt-10">
            No inward orders found.
          </p>
        )}

      {activeTab !== "finance-inward" &&
        outwardList &&
        outwardList.length === 0 &&
        !isLoading && (
          <p className="text-center text-muted-foreground mt-10">
            No outward orders found.
          </p>
        )}

      {/* PAGINATION */}
      {pagination && pagination.totalPages > 1 && page && setPage && (
        <div className="mt-6">
          <PaginationWarehouse
            page={page}
            totalPages={pagination.totalPages}
            onPageChange={(p: number) => setPage(p)}
          />
        </div>
      )}

      {/* MODALS */}
      {selectedOutward && (
        <GenerateDCModal
          open={isGenerateDCModalOpen}
          outward={selectedOutward}
          onClose={() => {
            setGenerateDCModalOpen(false);
            setSelectedOutward(null);
          }}
          onSuccess={() => {
            refreshOutwards?.();
            closeModals();
          }}
        />
      )}

      {selectedOutward && (
        <UploadInvModal
          open={isUploadInvModalOpen}
          outward={selectedOutward}
          onClose={() => {
            setUploadInvModalOpen(false);
            setSelectedOutward(null);
          }}
          onSuccess={() => {
            refreshOutwards?.();
            closeModals();
          }}
        />
      )}

      {selectedOutward && (
        <UploadReverseDcDocsModal
          open={isReverseDcDocsModalOpen}
          outward={selectedOutward}
          onClose={() => {
            setReverseDcDocsModalOpen(false);
            setSelectedOutward(null);
          }}
          onSuccess={() => {
            refreshOutwards?.();
            closeModals();
          }}
        />
      )}

      {selectedOutward && (
        <OutwardViewModal
          open={isViewModalOpen}
          onClose={() => setViewModalOpen(false)}
          outward={selectedOutward}
        />
      )}

      {selectedOutward && isRejectSTModalOpen && (
        <RejectSTModal
          open={isRejectSTModalOpen}
          onClose={() => {
            setIsRejectSTModalOpen(false);
            setSelectedOutward(null);
          }}
          onSuccess={() => {
            refreshOutwards?.();
            closeModals();
          }}
          selectedOutward={selectedOutward}
        />
      )}

      {isLoading && <LoadingWidget />}
    </main>
  );
};
